import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Alert,
} from '@mui/material';
import DescribedField from '../DescribedField';
import servicesService from '../../../services/servicesService';

function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return null;
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h${m}min` : `${h}h`;
}

function formatPrice(price) {
  if (!price || price <= 0) return null;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
}

/**
 * ServicesBlockEditor — Editor do bloco Serviços
 *
 * Exibe os serviços cadastrados pelo usuário para seleção simples.
 * Os selecionados são incluídos no bloco como snapshot dos dados do serviço.
 *
 * Props:
 *   - value: { items: [{ id, name, description, duration, price }] }
 *   - onChange: (patch) => void
 *   - context: { siteType, availableServices, ... }
 */
const ServicesBlockEditor = ({ value = {}, onChange, context = {} }) => {
  const [allServices, setAllServices] = useState(context.availableServices ?? null);
  const [loadError, setLoadError] = useState(false);

  const { items = [] } = value;
  const selectedIds = new Set(items.map((it) => it.id));

  // Se o contexto não injectou serviços, busca diretamente
  useEffect(() => {
    if (allServices !== null) return;
    servicesService.getAll()
      .then((data) => {
        const arr = Array.isArray(data) ? data : (data?.$values || []);
        setAllServices(arr);
      })
      .catch(() => {
        setAllServices([]);
        setLoadError(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync when parent context changes (e.g., user creates a new service while editor is open)
  useEffect(() => {
    if (context.availableServices) setAllServices(context.availableServices);
  }, [context.availableServices]);

  const toggle = (service) => {
    if (selectedIds.has(service.id)) {
      onChange({ ...value, items: items.filter((it) => it.id !== service.id) });
    } else {
      const snapshot = {
        id: service.id,
        name: service.displayName || service.name,
        description: service.description || '',
        duration: formatDuration(service.defaultDurationMinutes) ?? '',
        price: formatPrice(service.defaultPrice) ?? '',
      };
      onChange({ ...value, items: [...items, snapshot] });
    }
  };

  if (allServices === null) {
    return (
      <DescribedField
        label="Serviços"
        description="Selecione os serviços que deseja exibir nesta página."
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      </DescribedField>
    );
  }

  return (
    <DescribedField
      label="Serviços exibidos"
      description="Selecione quais serviços do seu catálogo aparecerão nesta página. Os dados (nome, duração e preço) são importados automaticamente do cadastro."
      helperLink={{ entity: 'service' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {loadError && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Não foi possível carregar seus serviços. Verifique sua conexão.
          </Alert>
        )}

        {allServices.length === 0 ? (
          <Alert severity="info">
            Nenhum serviço cadastrado. Cadastre seus serviços primeiro para selecioná-los aqui.
          </Alert>
        ) : (
          <List dense disablePadding>
            {allServices.map((service) => {
              const checked = selectedIds.has(service.id);
              const durationLabel = formatDuration(service.defaultDurationMinutes);
              const priceLabel = formatPrice(service.defaultPrice);

              return (
                <ListItem key={service.id} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <Checkbox
                      edge="start"
                      checked={checked}
                      onChange={() => toggle(service)}
                      size="small"
                      inputProps={{ 'aria-label': `Selecionar ${service.name}` }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <FormControlLabel
                        control={<span />}
                        label={
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body2" fontWeight={checked ? 600 : 400} component="span">
                              {service.displayName || service.name}
                            </Typography>
                            {durationLabel && (
                              <Chip label={durationLabel} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                            )}
                            {priceLabel && (
                              <Chip label={priceLabel} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                            )}
                          </Box>
                        }
                        sx={{ m: 0 }}
                        onClick={() => toggle(service)}
                      />
                    }
                    secondary={service.description || null}
                    secondaryTypographyProps={{ variant: 'caption', sx: { mt: 0.25, display: 'block' } }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        {items.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {items.length} serviço{items.length !== 1 ? 's' : ''} selecionado{items.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>
    </DescribedField>
  );
};

ServicesBlockEditor.propTypes = {
  value: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        duration: PropTypes.string,
        price: PropTypes.string,
      })
    ),
  }),
  onChange: PropTypes.func.isRequired,
  context: PropTypes.object,
};

export default ServicesBlockEditor;
